// src/logic/req.js
import axios from 'axios';
import auth from '@/logic/auth';
import router from '@/router';

// Instancia Axios con base URL desde .env
const api = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
});

// Interceptor para añadir Authorization header
api.interceptors.request.use(
    async config => {
        const token = await auth.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    error => Promise.reject(error)
);

// Interceptor para gestionar refresh de token y logout
api.interceptors.response.use(
    response => response,
    async error => {
        const { config, response } = error;
        // Intentar refresh una sola vez si status 401
        if (response ?.status === 401 && !config._retry) {
            config._retry = true;
            const refreshToken = await auth.getRefreshToken();
            if (refreshToken) {
                try {
                    const res = await api.post('refresh', { refresh_token: refreshToken });
                    const newToken = res.data.access_token;
                    await auth.setToken(newToken);
                    config.headers.Authorization = `Bearer ${newToken}`;
                    return api.request(config);
                } catch (_) {
                    // refresh falló, se limpiará más abajo
                }
            }
        }

        // Para 401 o 422 sin posibilidad de refresh
        if ([401, 422].includes(response ?.status)) {
            await auth.clearSession();
            router.push({ name: 'login' });
        }
        return Promise.reject(error);
    }
);

export default {
    // Mantiene firma original: recibe URL y devuelve la respuesta completa
    async get(url) {
        const response = await api.get(url);
        return response;
    },

    async get_params(url, params) {
        const response = await api.get(url, { params: JSON.stringify(params) });
        return response;
    },

    async post(url, data) {
        const response = await api.post(url, data, {
            headers: { 'Content-Type': 'application/json' }
        });
        return response;
    },

    async put(url, data) {
        const response = await api.put(url, data, {
            headers: { 'Content-Type': 'application/json' }
        });
        return response;
    },

    async post_file(url, file, paramName = null, paramValue = null) {
        const formData = new FormData();
        formData.append('file', file);
        
        // Si se proporcionan parámetros, añadirlos a la URL
        let fullUrl = url;
        if (paramName && paramValue) {
            fullUrl = `${url}?${paramName}=${paramValue}`;
        }
        
        const response = await api.post(fullUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response;
    },

    async delete(url) {
        const response = await api.delete(url);
        return response;
    }
};